// Campaign + brief API — TanStack Query hooks (PRD-0011)

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import type {
  Brief,
  BriefDocType,
  BriefDocument,
  BriefEvent,
  Campaign,
  ContentPacket,
} from "../../../shared/types";
import type { SourceRiskAcknowledgementInput } from "../../../shared/review-source-risk";

/** List row includes per-state brief counts from the campaigns list endpoint */
export type CampaignWithCounts = Campaign & {
  brief_counts: Record<string, number>;
};

export type CampaignDetail = Campaign & { briefs: Brief[] };

export function useCampaignList(brandId: string | undefined) {
  return useQuery({
    queryKey: ["campaigns", brandId],
    queryFn: () =>
      api.get<{ data: CampaignWithCounts[] }>(`/api/campaigns?brand_id=${encodeURIComponent(brandId!)}`),
    enabled: !!brandId,
  });
}

export function useCampaignDetail(brandId: string | undefined, campaignId: string | undefined) {
  return useQuery({
    queryKey: ["campaigns", brandId, campaignId],
    queryFn: () => api.get<{ data: CampaignDetail }>(`/api/campaigns/${campaignId}`),
    enabled: !!brandId && !!campaignId,
  });
}

export function useCreateCampaign(brandId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      brand_id: string;
      title: string;
      goal: string;
      strategy_pillar?: string | null;
      deadline?: string | null;
      starts_at: string | null;
      ends_at: string | null;
    }) => api.post<{ data: Campaign }>("/api/campaigns", body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["campaigns", brandId] });
    },
  });
}

export function useUpdateCampaign(brandId: string | undefined, campaignId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { title: string; goal: string; deadline: string | null; starts_at: string | null; ends_at: string | null; expected_updated_at: string; strategy_pillar?: string | null }) => {
      if (!brandId || !campaignId) throw new Error("Missing campaign context");
      return api.patch<{ data: Campaign }>(`/api/campaigns/${encodeURIComponent(campaignId)}`, { ...body, brand_id: brandId });
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["campaigns", brandId] }); },
  });
}

export function useCreateBrief(brandId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      campaignId,
      ...body
    }: {
      campaignId: string;
      title: string;
      platform?: string | null;
      format?: string | null;
      due_at?: string | null;
      surface?: Brief["surface"];
      scheduled_at?: string | null;
    }) => {
      if (!campaignId) throw new Error("Missing campaignId");
      return api.post<{ data: Brief }>(`/api/campaigns/${campaignId}/briefs`, body);
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ["campaigns", brandId, variables.campaignId] });
      qc.invalidateQueries({ queryKey: ["campaigns", brandId] });
      qc.invalidateQueries({ queryKey: ["brand-review", brandId] });
    },
  });
}

export function useUpdateBrief(campaignId: string | undefined, brandId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      briefId,
      ...body
    }: {
      briefId: string;
      title?: string | null;
      platform?: string | null;
      format?: string | null;
      due_at?: string | null;
      surface?: Brief["surface"] | null;
      scheduled_at?: string | null;
    }) => {
      if (!campaignId || !brandId) throw new Error("Missing campaign or brand context");
      return api.patch<{ data: Brief }>(`/api/campaigns/${campaignId}/briefs/${briefId}`, body);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["campaigns", brandId, campaignId] });
      qc.invalidateQueries({ queryKey: ["campaigns", brandId] });
      qc.invalidateQueries({ queryKey: ["brand-review", brandId] });
    },
  });
}

export function useCampaignPackets(campaignId: string | undefined, brandId: string | undefined) {
  return useQuery({
    queryKey: ["campaign-packets", brandId, campaignId],
    queryFn: () => api.get<{ data: ContentPacket[] }>(`/api/campaigns/${campaignId}/packets`),
    enabled: !!campaignId && !!brandId,
  });
}

export function useCreateCampaignPacket(campaignId: string | undefined, brandId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      campaignId: overrideCampaignId,
      ...body
    }: {
      campaignId?: string;
      title: string;
      draft_body: string;
      status?: ContentPacket["status"];
      platform?: string | null;
      format?: string | null;
      surface?: Brief["surface"] | null;
      angle_brief?: string | null;
      review_report?: string | null;
      approval_packet?: string | null;
      source_refs?: Array<{ label?: string | null; url: string }>;
      metadata?: Record<string, unknown>;
    }) => {
      const resolvedCampaignId = overrideCampaignId ?? campaignId;
      if (!resolvedCampaignId || !brandId) throw new Error("Missing campaign or brand context");
      return api.post<{ data: ContentPacket }>(`/api/campaigns/${resolvedCampaignId}/packets`, body);
    },
    onSuccess: (_data, variables) => {
      const resolvedCampaignId = variables.campaignId ?? campaignId;
      if (resolvedCampaignId) {
        qc.invalidateQueries({ queryKey: ["campaigns", brandId, resolvedCampaignId] });
        qc.invalidateQueries({ queryKey: ["campaign-packets", brandId, resolvedCampaignId] });
      }
      qc.invalidateQueries({ queryKey: ["campaigns", brandId] });
      qc.invalidateQueries({ queryKey: ["brand-review", brandId] });
    },
  });
}

/** PRD-0019 — save chat-formatted content as brief/draft document (NoSQL revision). */
export function useSaveBriefDocument(brandId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      campaignId,
      briefId,
      type,
      content,
      metadata,
      manual_canonical_brief,
    }: {
      campaignId: string;
      briefId: string;
      type: "draft" | "brief";
      content: string;
      metadata?: Record<string, unknown>;
      manual_canonical_brief?: Record<string, unknown>;
    }) =>
      api.put<{ data: { brief_id: string; type: string; revision: number } }>(
        `/api/campaigns/${campaignId}/briefs/${briefId}/document`,
        { type, content, metadata, ...(manual_canonical_brief ? { manual_canonical_brief } : {}) },
      ),
    onSuccess: (_data, v) => {
      qc.invalidateQueries({ queryKey: ["campaigns", brandId, v.campaignId] });
      qc.invalidateQueries({ queryKey: ["brief-document", v.campaignId, v.briefId] });
      qc.invalidateQueries({ queryKey: ["brand-review", brandId] });
    },
  });
}

export function useApproveBrief(campaignId: string | undefined, brandId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (
      input: string | { briefId: string; sourceRiskAcknowledgement?: SourceRiskAcknowledgementInput },
    ) => {
      if (!campaignId || !brandId) throw new Error("Missing campaign or brand context");
      const briefId = typeof input === "string" ? input : input.briefId;
      const sourceRiskAcknowledgement = typeof input === "string" ? undefined : input.sourceRiskAcknowledgement;
      return api.post<{ data: unknown }>(
        `/api/campaigns/${campaignId}/briefs/${briefId}/approve`,
        sourceRiskAcknowledgement
          ? { source_risk_acknowledgement: sourceRiskAcknowledgement }
          : {},
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["campaigns", brandId, campaignId] });
      qc.invalidateQueries({ queryKey: ["campaigns", brandId] });
      qc.invalidateQueries({ queryKey: ["brand-review", brandId] });
      qc.invalidateQueries({ queryKey: ["brand-review-approved", brandId] });
      qc.invalidateQueries({ queryKey: ["brief-approval", campaignId] });
    },
  });
}

export function useRequestBriefChanges(campaignId: string | undefined, brandId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ briefId, reason }: { briefId: string; reason: string }) => {
      if (!campaignId || !brandId) throw new Error("Missing campaign or brand context");
      return api.post<{ data: unknown }>(
        `/api/campaigns/${campaignId}/briefs/${briefId}/request-changes`,
        {
          reason,
        },
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["campaigns", brandId, campaignId] });
      qc.invalidateQueries({ queryKey: ["campaigns", brandId] });
      qc.invalidateQueries({ queryKey: ["brand-review", brandId] });
    },
  });
}

export function useRejectBrief(campaignId: string | undefined, brandId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ briefId, reason }: { briefId: string; reason?: string }) => {
      if (!campaignId || !brandId) throw new Error("Missing campaign or brand context");
      return api.post<{ data: unknown }>(
        `/api/campaigns/${campaignId}/briefs/${briefId}/reject`,
        { reason: reason ?? null },
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["campaigns", brandId, campaignId] });
      qc.invalidateQueries({ queryKey: ["campaigns", brandId] });
      qc.invalidateQueries({ queryKey: ["brand-review", brandId] });
    },
  });
}

export function useNudgeBrief(campaignId: string | undefined, brandId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ briefId, message }: { briefId: string; message?: string }) => {
      if (!campaignId || !brandId) throw new Error("Missing campaign or brand context");
      return api.post<{ data: { audit: unknown; chat_path: string } }>(
        `/api/campaigns/${campaignId}/briefs/${briefId}/nudge`,
        { message: message ?? null },
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["brand-review", brandId] });
    },
  });
}

export function useSubmitPublishedUrl(campaignId: string | undefined, brandId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ briefId, publishedUrl }: { briefId: string; publishedUrl: string }) => {
      if (!campaignId || !brandId) throw new Error("Missing campaign or brand context");
      return api.post<{ data: { brief: Brief | null; events: BriefEvent[] } }>(
        `/api/campaigns/${campaignId}/briefs/${briefId}/published-url`,
        { published_url: publishedUrl },
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["campaigns", brandId, campaignId] });
      qc.invalidateQueries({ queryKey: ["campaigns", brandId] });
      qc.invalidateQueries({ queryKey: ["brand-review", brandId] });
    },
  });
}

/**
 * Round-3 P0-2: Retry on a Draft this brief drafts it again (the server returns it to briefed and starts
 * a run) instead of requeueing it to ideation, so say that rather than "back in progress".
 */
export function retryNotice(result: unknown): string {
  const data = result && typeof result === "object" ? (result as { data?: unknown }).data : null;
  const mode = data && typeof data === "object" ? (data as { mode?: unknown }).mode : null;
  return mode === "draft_this"
    ? "Drafting again. The draft appears in Review when its quality checks pass; follow it on the brief page."
    : "Retry started. The action is back in progress.";
}

export function useRetryBrief(campaignId: string | undefined, brandId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (briefId: string) => {
      if (!campaignId || !brandId) throw new Error("Missing campaign or brand context");
      return api.post<{ data: unknown }>(
        `/api/campaigns/${campaignId}/briefs/${briefId}/retry`,
        {},
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["campaigns", brandId, campaignId] });
      qc.invalidateQueries({ queryKey: ["campaigns", brandId] });
      qc.invalidateQueries({ queryKey: ["brand-review", brandId] });
      // The blocked row in Inbox / campaign work comes from operator-work.
      qc.invalidateQueries({ queryKey: ["operator-work", brandId] });
      // A Draft this retry starts a run the brief page's latest-attempt panel follows.
      qc.invalidateQueries({ queryKey: ["brief-draft-attempt", campaignId] });
    },
  });
}

export function useSkipBrief(campaignId: string | undefined, brandId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ briefId, reason }: { briefId: string; reason?: string }) => {
      if (!campaignId || !brandId) throw new Error("Missing campaign or brand context");
      return api.post<{ data: unknown }>(
        `/api/campaigns/${campaignId}/briefs/${briefId}/skip`,
        { reason: reason ?? null },
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["campaigns", brandId, campaignId] });
      qc.invalidateQueries({ queryKey: ["campaigns", brandId] });
      qc.invalidateQueries({ queryKey: ["brand-review", brandId] });
      // The blocked row in Inbox / campaign work comes from operator-work.
      qc.invalidateQueries({ queryKey: ["operator-work", brandId] });
    },
  });
}

export function useDeleteBrief(campaignId: string | undefined, brandId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (briefId: string) => {
      if (!campaignId || !brandId) throw new Error("Missing campaign or brand context");
      return api.delete<{ data: { id: string; deleted: boolean } }>(
        `/api/campaigns/${campaignId}/briefs/${briefId}`,
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["campaigns", brandId, campaignId] });
      qc.invalidateQueries({ queryKey: ["campaigns", brandId] });
      qc.invalidateQueries({ queryKey: ["brand-review", brandId] });
    },
  });
}

export function useBriefDocument(
  campaignId: string | undefined,
  briefId: string | undefined,
  docType: BriefDocType | undefined,
  enabled: boolean,
) {
  return useQuery({
    queryKey: ["brief-document", campaignId, briefId, docType],
    queryFn: () =>
      api.get<{ data: BriefDocument }>(
        `/api/campaigns/${campaignId}/briefs/${briefId}/documents/${docType}`,
      ),
    enabled: !!campaignId && !!briefId && !!docType && enabled,
    retry: false,
  });
}

export function useBriefEvents(
  campaignId: string | undefined,
  briefId: string | undefined,
  enabled: boolean,
) {
  return useQuery({
    queryKey: ["brief-events", campaignId, briefId],
    queryFn: () =>
      api.get<{ data: BriefEvent[] }>(`/api/campaigns/${campaignId}/briefs/${briefId}/events`),
    enabled: !!campaignId && !!briefId && enabled,
  });
}
